import { RJSFSchema, UiSchema } from "@rjsf/utils";
import { customizeValidator } from "@rjsf/validator-ajv8";
import JSONSchemaForm from "@rjsf/bootstrap-4";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState, useCallback, useRef } from "react";
import customFormats from "@beeldit/core/custom-validations/custom-validation";
import useProcessSchema from "@beeldit/core/hooks/useProcessSchema";
import widgets from "@beeldit/core/widgets/widgets";
import { ConfigurationsContext } from "@main/contexts/ConfigurationsContext";
import { ButtonGroup, ToggleButton, Modal, Button } from "react-bootstrap";
import index from "@beeldit/core/services";
import checkRoles from "@beeldit/user-and-access/auth/services/checkRoles";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import post from "@beeldit/core/services/store";
import MultidimensionalCustomFormTemplate from "@beeldit/core/templates/MultidimensionalCustomFormTemplate";

interface Prop {
  formConfig: {
    formRef: any;
    onSubmit: any;
    context: any;
    className?: string;
    showDiscount?: any;
  };
}

interface Context {
  element: any;
  setElement: (element: any) => any;
  backendFormErrors: any;
  selectedLang: string;
  setSelectedLang: (lang: string) => any;
  attributes: any[];
  setAttributes: (attributes: any) => void;
  commercePrice: number;
  setCommercePrice: (price: number) => void;
  shippingPercentage: number;
  setShippingPercentage: (percentage: number) => void;
  commissionPercentage: number;
  setCommissionPercentage: (percentage: number) => void;
  adjustmentError: any;
  setAdjustmentError: (error: any) => void;
}

function CombinationForm(props: Prop) {
  const { formRef, onSubmit, context, className, showDiscount } =
    props.formConfig;
  const {
    element,
    setElement,
    selectedLang,
    setSelectedLang,
    backendFormErrors,
    attributes,
    setAttributes,
    adjustmentError,
    setAdjustmentError,
  } = useContext<Context>(context);

  const { langs } = useContext<any>(ConfigurationsContext);
  const { user } = useContext(AuthContext);

  const { t } = useTranslation();

  const [finalSchema, setFinalSchema] = useState<RJSFSchema>({});
  const [finalUiSchema, setFinalUiSchema] = useState<UiSchema>({});
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState<{
    field: 'price' | 'commission_percentage';
    value: number;
    fieldLabel: string;
  } | null>(null);

  // Detectar errores especiales del backend para ajuste automático
  useEffect(() => {
    if (!adjustmentError) return;
    
    if (adjustmentError.new_price) {
      const suggestedPrice = parseFloat(adjustmentError.new_price);
      if (!isNaN(suggestedPrice)) {
        setAdjustmentData({
          field: 'price',
          value: suggestedPrice,
          fieldLabel: t('price')
        });
        setShowAdjustModal(true);
      }
    } else if (adjustmentError.new_commission_percentage) {
      const suggestedCommission = parseFloat(adjustmentError.new_commission_percentage);
      if (!isNaN(suggestedCommission)) {
        setAdjustmentData({
          field: 'commission_percentage',
          value: suggestedCommission,
          fieldLabel: t('commission_percentage')
        });
        setShowAdjustModal(true);
      }
    }
  }, [adjustmentError, t]);

  const translations = {
    name: t("name"),
    stock: t("stock"),
    price: t("price"),
    commerce_price: t("commerce_price"),
    tax: t("tax"),
    tax_percentage: t("tax_percentage"),
    sku: t("sku"),
    price_with_descount: t("price_with_descount"),
    attributes: t("attributes"),
    gallery: t("gallery"),
    image_alt: t("image_alt"),
    img: t("img"),
    main: t("main"),
    commission: t("commission"),
    commission_percentage: t("commission_percentage"),
    shipping: t("shipping"),
    shipment_percentage: t("shipment_percentage"),
    role_segment: t("role_segment"),
  };

  let schema: any = {
    type: "object",
    properties: {
      name: {
        title: "",
        type: "object",
        properties: {},
      },
      sku: {
        type: "string",
        title: translations.sku,
      },
      attributes: {
        title: "",
        type: ["object", "array"],
        properties: {},
      },
      stock: {
        type: "number",
        title: translations.stock,
        min: -1,
        step: 1,
      },
      price: {
        type: "number",
        title: translations.price,
        min: 0,
        step: 0.01,
      },
      tax_percentage: {
        type: "number",
        title: translations.tax_percentage,
      },
      commission_percentage: {
        type: ["number"],
        title: translations.commission_percentage,
      },
      commission: {
        type: "number",
        title: translations.commission,
      },
      shipment_percentage: {
        type: ["number"],
        title: translations.shipment_percentage,
      },
      shipping: {
        type: "number",
        title: translations.shipping,
      },
      commerce_price: {
        type: "number",
        title: translations.commerce_price,
        min: 0,
        step: 0.01,
      },
      price_with_descount: {
        type: ["number", "null"],
        title: translations.price_with_descount,
        min: 0,
        step: 0.01,
        show: showDiscount,
      },
      role_segment: {
        type: ["string", "null"],
        title: translations.role_segment,
        default: null,
        oneOf: [
          {
            const: null,
            title: "Todos",
          },
          {
            const: "dealer",
            title: "Distribuidor",
          },
        ],
        show: !element.main
      },
      gallery: {
        title: translations.gallery,
        type: "array",
        items: {
          type: "object",
          properties: {
            alt: {
              type: "object",
              properties: {},
              title: "",
            },
            img: {
              type: "array",
              title: "",
              items: {
                type: "object",
                properties: {
                  filename: {
                    type: "string",
                  },
                  url: {
                    type: "string",
                  },
                },
              },
            },
            main: {
              type: "boolean",
              title: translations.main,
            },
          },
        },
      },
    },
  };
  const uiSchema: UiSchema = {
    "ui:classNames": className,
    "ui:submitButtonOptions": {
      props: {
        disabled: false,
        className: "btn btn-info",
      },
      norender: true,
    },
    name: {},
    attributes: {},
    gallery: {
      items: {
        img: {
          "ui:widget": "multiple-file-input",
          "ui:options": {
            label: false,
          },
        },
        alt: {},
        main: {
          "ui:widget": "radio-button",
          "ui:options": {
            "radio-name": "main",
            label: false,
          },
        },
      },
    },
    commission_percentage: {
      "ui:disabled": !checkRoles(user, ["admin"]),
      ...(checkRoles(user, ["admin"]) ? {} : { "ui:widget": "hidden" }),
    },
    commission: {
      "ui:disabled": true,
      ...(checkRoles(user, ["admin"]) ? {} : { "ui:widget": "hidden" }),
    },
    shipment_percentage: {
      "ui:disabled": !checkRoles(user, ["admin"]),
    },
    shipping: {
      "ui:disabled": true,
    },
    commerce_price: {
      "ui:disabled": !checkRoles(user, ["admin"]),
    },
    tax_percentage: {
      "ui:widget": "type-selector",
      "ui:options": {
        configPath: "types.taxs.tax",
      },
    },
    colsSchema: [
      {
        cols: "col-12",
        elements: [
          [
            {
              name: "name",
              cols: "col-6",
            },
            {
              name: "sku",
              cols: "col-6",
            },
            {
              name: "attributes",
              cols: "col-6",
            },
            {
              name: "stock",
              cols: "col-6",
            },
            {
              name: "price",
              cols: "col-6",
            },
            {
              name: "tax_percentage",
              cols: "col-6",
            },
            {
              name: "commission_percentage",
              cols: "col-6",
            },
            {
              name: "commission",
              cols: "col-6",
            },
            {
              name: "commerce_price",
              cols: "col-6",
            },
            {
              name: "price_with_descount",
              cols: "col-6",
            },
            {
              name: "shipment_percentage",
              cols: "col-6",
            },
            {
              name: "shipping",
              cols: "col-6",
            },
            {
              name: "role_segment",
              cols: "col-6",
            },
            {
              name: "gallery",
              cols: "col-12",
            },
          ],
        ],
      },
    ],
  };
  const customWidgets = {
    ...widgets,
  };
  const handleChange = (type: any) => {
    /** Este método de momento es necesario aunque en realidad no tenga sentido, pero se debe a un bug de la libreria */
    // Actualizar el estado solo si no hay errores de validación
    setElement(type.formData);
    return console.log.bind(console, type);
  };
  const log = (type: any) => {
    return console.log.bind(console, type);
  };

  const getAttributes = useCallback(() => {
    index("attributes", "relations=options&combination=1").then(
      (response: any) => {
        setAttributes(response.data.elements);
      }
    );
  }, [setAttributes]);

  const isCalculatingRef = useRef(false);
  const elementRef = useRef(element);
  
  useEffect(() => {
    elementRef.current = element;
  }, [element]);

  const getCommercePrice = useCallback(() => {
    if (isCalculatingRef.current) return;
    isCalculatingRef.current = true;
    
    const currentElement = elementRef.current;
    if (!currentElement) {
      isCalculatingRef.current = false;
      return;
    }
    
    post("combinations/commerce-price", {
      price: currentElement.price ? currentElement.price : 0,
      tax_percentage: currentElement.tax_percentage ? currentElement.tax_percentage : 0,
      shipment_percentage: currentElement.shipment_percentage,
      commission_percentage: currentElement.commission_percentage,
    }).then((response: any) => {
      const newCommercePrice = response.data.price;
      const currentCommercePrice = elementRef.current?.commerce_price || 0;
      
      // Solo actualizar commerce_price si la diferencia es mayor a 1 céntimo
      const shouldUpdateCommercePrice = Math.abs(newCommercePrice - currentCommercePrice) > 0.01;
      
      const updatedElement = {
        ...elementRef.current,
        commerce_price: shouldUpdateCommercePrice ? newCommercePrice : currentCommercePrice,
        commission: response.data.commission,
        shipping: response.data.shipment,
        tax: response.data.tax || 0,
        calculated_commerce_price: response.data.calculated_commerce_price || response.data.price || 0,
      };
      
      console.log('getCommercePrice - Actualizando element:', {
        commission: response.data.commission,
        shipping: response.data.shipment,
        commerce_price: shouldUpdateCommercePrice ? newCommercePrice : currentCommercePrice,
        tax: response.data.tax,
        calculated_commerce_price: response.data.calculated_commerce_price
      });
      
      setElement(updatedElement);
      isCalculatingRef.current = false;
    }).catch(() => {
      isCalculatingRef.current = false;
    });
  }, [setElement]);

  useEffect(() => {
    getAttributes();
  }, [getAttributes]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!element || element.price === undefined) return;
    getCommercePrice();
  }, [element?.price, getCommercePrice]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!element) return;
    getCommercePrice();
  }, [element?.tax_percentage, getCommercePrice]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!element || element.shipment_percentage === undefined) return;
    getCommercePrice();
  }, [element?.shipment_percentage, getCommercePrice]);
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!element || element.commission_percentage === undefined) return;
    getCommercePrice();
  }, [element?.commission_percentage, getCommercePrice]);

  const formats = customFormats;
  const validator = customizeValidator({ customFormats: formats });

  const processSchemaFunction = useProcessSchema();

  const setSchemaLangFields = () => {
    let new_names: any = {};
    let new_images_alt: any = {};

    for (let lang of langs) {
      new_names[lang] = {
        type: "string",
        title: t("name"),
      };
      new_images_alt[lang] = {
        type: ["string", "null"],
        title: t("image_alt"),
      };
      if (selectedLang !== lang) {
        uiSchema["name"][lang] = { "ui:widget": "hidden" };
        uiSchema["gallery"]["items"]["alt"][lang] = { "ui:widget": "hidden" };
      }
    }
    let new_attributes: any = {};
    for (let attribute of attributes) {
      new_attributes["attribute_" + attribute.id] = {
        type: ["string", "null"],
        title: attribute.name,
        anyOf: [
          { title: "-", type: "null", enum: [null] },
          ...attribute.options.map((option: any) => ({
            title: option.name ?? t(option.name_lang_code),
            type: "string",
            enum: [option.name_lang_code],
          })),
        ],
      };
      uiSchema["attributes"]["attribute_" + attribute.id] = {
        "ui:placeholder": t("select_an_option"),
      };
    }
    schema.properties.name.properties = new_names;
    schema.properties.gallery.items.properties.alt.properties = new_images_alt;
    schema.properties.attributes.properties = new_attributes;
    setFinalSchema(processSchemaFunction(schema, element));
    setFinalUiSchema(uiSchema);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setSchemaLangFieldsCallback = useCallback(setSchemaLangFields, [langs, element, selectedLang]);
  useEffect(() => {
    setSchemaLangFieldsCallback();
  }, [setSchemaLangFieldsCallback]);

  const formTemplate = MultidimensionalCustomFormTemplate;

  const handleConfirmAdjustment = async () => {
    if (!adjustmentData) return;
    
    // Aplicar el ajuste al elemento
    const updatedElement = {
      ...element,
      [adjustmentData.field]: adjustmentData.value
    };
    
    // Cerrar modal y limpiar datos inmediatamente para evitar múltiples apariciones
    setShowAdjustModal(false);
    setAdjustmentData(null);
    setAdjustmentError(null);
    
    // Actualizar el elemento en el estado
    setElement(updatedElement);
    elementRef.current = updatedElement;
    
    try {
      // Llamar a getCommercePrice para recalcular commission, shipping, commerce_price
      const response = await post("combinations/commerce-price", {
        price: updatedElement.price ? updatedElement.price : 0,
        tax_percentage: updatedElement.tax_percentage ? updatedElement.tax_percentage : 0,
        shipment_percentage: updatedElement.shipment_percentage,
        commission_percentage: updatedElement.commission_percentage,
      });
      
      const newCommercePrice = response.data.price;
      const currentCommercePrice = updatedElement.commerce_price || 0;
      
      // Solo actualizar commerce_price si la diferencia es MAYOR a 1 céntimo
      const shouldUpdateCommercePrice = Math.abs(newCommercePrice - currentCommercePrice) > 0.01;
      
      // Crear el elemento final con todos los valores actualizados
      const finalElement = {
        ...updatedElement,
        commerce_price: shouldUpdateCommercePrice ? newCommercePrice : currentCommercePrice,
        commission: response.data.commission,
        shipping: response.data.shipment,
        tax: response.data.tax || 0,
        calculated_commerce_price: response.data.calculated_commerce_price || response.data.price || 0,
      };
      
      // Actualizar el estado con los valores calculados
      setElement(finalElement);
      elementRef.current = finalElement;
      
      // Ahora sí, guardar con todos los valores correctos
      setTimeout(() => {
        onSubmit({ formData: finalElement });
      }, 100);
    } catch (error) {
      console.error("Error recalculando commerce price:", error);
    }
  };

  const handleCancelAdjustment = () => {
    setShowAdjustModal(false);
    setAdjustmentData(null);
    setAdjustmentError(null);
  };

  return (
    <>
      <div className="text-right mb-3">
        {
          <ButtonGroup>
            {langs.map((lang: string, index: number) => (
              <ToggleButton
                key={index}
                id={`combination-${index}`}
                type="radio"
                variant="outline-primary"
                name="radio"
                value={lang}
                checked={selectedLang === lang}
                onChange={(e) => setSelectedLang(e.currentTarget.value)}
              >
                {t(lang)}
              </ToggleButton>
            ))}
          </ButtonGroup>
        }
      </div>
      {!checkRoles(user, ["admin"]) && element?.commission_percentage > 0 && (
        <div className="alert alert-info py-2 mb-3">
          <small>
            {t("commission_info_prefix")}{" "}
            <strong>{element.commission_percentage}%</strong>{" "}
            {t("commission_info_suffix")}
          </small>
        </div>
      )}
      <JSONSchemaForm
        noHtml5Validate
        showErrorList={false}
        ref={formRef}
        templates={{
          ObjectFieldTemplate: formTemplate,
        }}
        schema={finalSchema}
        uiSchema={finalUiSchema}
        widgets={customWidgets}
        formData={element}
        validator={validator}
        onChange={handleChange}
        onSubmit={onSubmit}
        onError={log("errors")}
        extraErrors={backendFormErrors}
      />
      
      {/* Modal de ajuste de valores inconsistentes */}
      <Modal show={showAdjustModal} onHide={handleCancelAdjustment} centered>
        <Modal.Header closeButton>
          <Modal.Title>Ajuste de valores</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {adjustmentData && (
            <p>
              El valor del <strong>PVP</strong> no es consistente con el campo{" "}
              <strong>{adjustmentData.fieldLabel}</strong>. ¿Deseas que ajustemos el valor del campo{" "}
              <strong>{adjustmentData.fieldLabel}</strong> a{" "}
              <strong>{adjustmentData.value.toFixed(2)}</strong> para continuar?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelAdjustment}>
            {t("no")}
          </Button>
          <Button variant="primary" onClick={handleConfirmAdjustment}>
            {t("yes")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CombinationForm;
